import React from 'react';
import { Carousel } from 'primereact/carousel';
import { Controller } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { RadioButton } from 'primereact/radiobutton';
import BaseCard from '../misc/BaseCard.jsx';
import companyImage from '../../resources/images/company.png';

const SimpleInput = ({
    id,
    name,
    value,
    onChange,
    onKeyDown,
    placeholder,
    required = false,
    disabled = false,
    readOnly = false,
    className,
    inputClassName
}) => {
    return (
        <input
            id={id ?? name}
            name={name}
            value={value}
            onChange={onChange}
            onKeyDown={onKeyDown}
            required={required}
            disabled={disabled}
            readOnly={readOnly}
            className={twMerge(
                'text-theme1-text1 rounded-2xl border px-4 py-2 border-theme1-text3 w-full outline-none',
                inputClassName,
                className
            )}
            placeholder={placeholder}
        />
    );
};

const SimpleRadio = ({
    id,
    name,
    value,
    checked,
    onChange,
    textValue,
    disabled = false,
    onKeyDown,
    required,
    readOnly,
    childrenClassName
}) => {
    return (
        <div className='flex items-center'>
            <RadioButton
                inputId={id ?? name}
                name={name}
                value={value}
                checked={checked}
                disabled={disabled}
                onChange={onChange}
                onKeyDown={onKeyDown}
                required={required}
                readOnly={readOnly}
            />
        </div>
    );
};

const ContactCompaniesCarouselDuplicates = ({ companiesData, control }) => {
    const companies = companiesData ? Object.values(companiesData) : [];

    const renderField = (company, fieldName) => {
        const fieldValues = company[fieldName] || [];
        const fieldKey = company.organization_id;

        if (fieldValues.length > 1) {
            return (
                <div className='flex flex-col space-y-2'>
                    <Controller
                        key={`companies-${fieldKey}-selected_${fieldName}`}
                        name={`companies.${fieldKey}.selected_${fieldName}`}
                        control={control}
                        defaultValue={0}
                        render={({ field: radioField }) => (
                            <>
                                {fieldValues.map((option, i) => (
                                    <div
                                        key={`companies-${fieldKey}-${fieldName}-${i}`}
                                        className='flex items-center space-x-2'
                                    >
                                        <SimpleRadio
                                            name={`companies.${fieldKey}.selected_${fieldName}`}
                                            value={i}
                                            checked={radioField.value === i}
                                            onChange={() => radioField.onChange(i)}
                                            textValue={''}
                                        />
                                        <Controller
                                            key={`companies-${fieldKey}-${fieldName}-input-${i}`}
                                            name={`companies.${fieldKey}.${fieldName}[${i}]`}
                                            control={control}
                                            defaultValue={option}
                                            render={({ field: textField }) => (
                                                <SimpleInput
                                                    value={textField.value}
                                                    onChange={(e) => textField.onChange(e.target.value)}
                                                    placeholder={`Enter ${fieldName.replace('_', ' ')}`}
                                                    inputClassName='p-1 pl-0 text-theme1-text1 border-t-0 border-l-0 border-r-0 rounded-none border-b border-theme1-cardStroke'
                                                />
                                            )}
                                        />
                                    </div>
                                ))}
                            </>
                        )}
                    />
                </div>
            );
        } else {
            const defaultValue = fieldValues.length === 1 ? fieldValues[0] : '';
            return (
                <Controller
                    key={`companies-${fieldKey}-${fieldName}-single`}
                    name={`companies.${fieldKey}.${fieldName}[0]`}
                    control={control}
                    defaultValue={defaultValue}
                    render={({ field: textField }) => (
                        <SimpleInput
                            value={textField.value}
                            onChange={(e) => textField.onChange(e.target.value)}
                            placeholder={`Enter ${fieldName.replace('_', ' ')}`}
                            inputClassName='p-1 pl-0 text-theme1-text1 border-t-0 border-l-0 border-r-0 rounded-none border-b border-theme1-cardStroke'
                        />
                    )}
                />
            );
        }
    };

    const companyTemplate = (company) => {
        return (
            <BaseCard key={company.organization_id} className='mt-0 mb-6 shadow-none'>
                <div className='flex flex-row items-center mb-6 relative'>
                    <div className='mr-6 w-12 h-12 flex items-center justify-center'>
                        <img alt='Company logo' src={companyImage} className='w-12 h-auto' />
                    </div>
                    <div className='flex-grow'>
                        <div className='text-theme1-primary font-bold text-xl'>
                            <Link to={`/organizations/${company.organization_id}`}>{company.organization_name}</Link>
                        </div>
                    </div>
                </div>

                <div className='mb-3'>
                    <span className='font-bold'>Job Title: </span>
                    {renderField(company, 'job_title')}
                </div>
                <div className='mb-3'>
                    <span className='font-bold'>Work Email: </span>
                    {renderField(company, 'work_email')}
                </div>
                <div className='mb-3'>
                    <span className='font-bold'>Work Phone: </span>
                    {renderField(company, 'work_phone')}
                </div>
            </BaseCard>
        );
    };

    return (
        <>
            <div className='mb-2 font-bold text-lg'>
                <span>Companies</span>
            </div>
            <div className='mb-10'>
                <Carousel
                    value={companies}
                    numVisible={1}
                    numScroll={1}
                    itemTemplate={(company) => companyTemplate(company)}
                    showNavigators={false}
                    circular={false}
                    showIndicators
                />
            </div>
        </>
    );
};

export default ContactCompaniesCarouselDuplicates;
